import type { AdminUser } from '@/types/admin'

/**
 * The signed-in staff member, as this app knows them.
 *
 * Deliberately small. It used to persist a refresh token in localStorage and carry a
 * documented XSS-to-account-takeover trade-off; Firebase now owns tokens and keeps its
 * own in IndexedDB, so there is nothing here worth stealing and nothing to persist.
 *
 * What this holds is the answer the API gives to "what may this person do" — the role
 * from `admin_users`, which Firebase knows nothing about. It is re-fetched on every
 * sign-in and every token refresh, so a role change or a deactivation takes effect
 * without the user reloading the page.
 */

export type AuthStoreState = {
  user: AdminUser | null
}

const EMPTY_STATE: AuthStoreState = Object.freeze({ user: null })

let state: AuthStoreState = EMPTY_STATE
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

function setState(next: AuthStoreState): void {
  state = Object.freeze(next)
  emit()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Stable reference between changes — required by useSyncExternalStore. */
function getSnapshot(): AuthStoreState {
  return state
}

function setUser(user: AdminUser): void {
  setState({ user })
}

function clear(): void {
  setState(EMPTY_STATE)
}

export const authStore = {
  subscribe,
  getSnapshot,
  setUser,
  clear,
}
